// @ts-check

/** @file Reads dashboard display fields from Timelog frontend models. */

/** @import Timelog from "../../../frontend-models/timelog" */
/** @import {ProjectedTimelogDashboardRow} from "../../../../../shared/projected-timelog-reporting-types.js" */

/**
 * @typedef {InstanceType<typeof Timelog> | ProjectedTimelogDashboardRow} TimelogModel
 */

/**
 * Reads the timelog id.
 * @param {TimelogModel} timelog Timelog model.
 * @returns {string} Database id.
 */
export function timelogId(timelog) {
  return isProjectedTimelog(timelog) ? timelog.id : timelog.id()
}

/**
 * Reads the timelog comment as display text.
 * @param {TimelogModel} timelog Timelog model.
 * @returns {string} Comment text.
 */
export function timelogComment(timelog) {
  return isProjectedTimelog(timelog) ? timelog.comment || "" : timelog.comment() || ""
}

/**
 * Reads the timelog date as a YYYY-MM-DD string.
 * @param {TimelogModel} timelog Timelog model.
 * @returns {string | null} Calendar date or null.
 */
export function timelogDateString(timelog) {
  if (isProjectedTimelog(timelog)) return timelog.date
  const value = timelog.date()

  if (!value) return null
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`
}

/**
 * Reads whether the timelog has been invoiced.
 * @param {TimelogModel} timelog Timelog model.
 * @returns {boolean} Whether the timelog is invoiced.
 */
export function timelogInvoiced(timelog) {
  return isProjectedTimelog(timelog) ? timelog.invoiced : Boolean(timelog.invoiced())
}

/**
 * Reads the logged time in seconds.
 * @param {TimelogModel} timelog Timelog model.
 * @returns {number} Logged seconds.
 */
export function timelogTimeSeconds(timelog) {
  return isProjectedTimelog(timelog) ? Number(timelog.timeSeconds) || 0 : Number(timelog.timeSeconds()) || 0
}

/**
 * Reads the stored hourly rate in cents.
 * @param {TimelogModel} timelog Timelog model.
 * @returns {number} Hourly rate in cents.
 */
export function timelogPricePerHourCents(timelog) {
  return isProjectedTimelog(timelog) ? Number(timelog.pricePerHourCents) || 0 : Number(timelog.pricePerHourCents()) || 0
}

/**
 * Reads the stored hourly rate currency.
 * @param {TimelogModel} timelog Timelog model.
 * @returns {string} Currency code.
 */
export function timelogPricePerHourCurrency(timelog) {
  const currency = isProjectedTimelog(timelog) ? timelog.pricePerHourCurrency : timelog.pricePerHourCurrency()

  return typeof currency === "string" && currency.length > 0 ? currency : "DKK"
}

/**
 * Reads the preloaded task name.
 * @param {TimelogModel} timelog Timelog model.
 * @returns {string | null} Task name or null.
 */
export function timelogTaskName(timelog) {
  return isProjectedTimelog(timelog) ? timelog.taskName || null : timelog.task()?.name() || null
}

/**
 * Reads the timelog task id.
 * @param {TimelogModel} timelog Timelog model.
 * @returns {string | null} Task id or null.
 */
export function timelogTaskId(timelog) {
  const taskId = isProjectedTimelog(timelog) ? timelog.taskId : timelog.taskId()

  return taskId || null
}

/**
 * Reads the preloaded project id.
 * @param {TimelogModel} timelog Timelog model.
 * @returns {string | null} Project id or null.
 */
export function timelogProjectId(timelog) {
  const projectId = isProjectedTimelog(timelog) ? timelog.projectId : timelog.task()?.project()?.id()

  return projectId || null
}

/**
 * Reads the preloaded project name.
 * @param {TimelogModel} timelog Timelog model.
 * @returns {string | null} Project name or null.
 */
export function timelogProjectName(timelog) {
  return isProjectedTimelog(timelog) ? timelog.projectName || null : timelog.task()?.project()?.name() || null
}

/**
 * Reads the timelog user id.
 * @param {TimelogModel} timelog Timelog model.
 * @returns {string | null} User id or null.
 */
export function timelogUserId(timelog) {
  const userId = isProjectedTimelog(timelog) ? timelog.userId : timelog.userId()

  return userId || null
}

/**
 * Reads the preloaded user's email address.
 * @param {TimelogModel} timelog Timelog model.
 * @returns {string | null} Email address or null.
 */
export function timelogUserEmail(timelog) {
  return isProjectedTimelog(timelog) ? timelog.userEmail || null : timelog.user()?.email() || null
}

/**
 * Reads the preloaded user's first name.
 * @param {TimelogModel} timelog Timelog model.
 * @returns {string | null} First name or null.
 */
export function timelogUserFirstName(timelog) {
  return isProjectedTimelog(timelog) ? timelog.userFirstName || null : timelog.user()?.firstName() || null
}

/**
 * Reads the preloaded user's last name.
 * @param {TimelogModel} timelog Timelog model.
 * @returns {string | null} Last name or null.
 */
export function timelogUserLastName(timelog) {
  return isProjectedTimelog(timelog) ? timelog.userLastName || null : timelog.user()?.lastName() || null
}

/**
 * Detects the immutable projected transport shape.
 * @param {TimelogModel} timelog Candidate row.
 * @returns {timelog is ProjectedTimelogDashboardRow} Whether it is projected.
 */
function isProjectedTimelog(timelog) {
  return typeof /** @type {Partial<ProjectedTimelogDashboardRow>} */ (timelog).projectName === "string"
}
